import React, { useState, useEffect, useRef } from 'react';
import { oggiClinica } from '../../utils/oraItaliana';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { pazientiAPI } from '../../services/api';
import PazienteForm from './PazienteForm';
import ModulisticaWizard from './ModulisticaWizard';
import NotaInline from './NotaInline';
import { useAuthStore } from '../../store/useStore';
import { puoAccettarePazienti, puoModificareAnagrafica, FRONT_DESK } from '../../utils/permessi';
import { Search, Clock, Download, Contact, FilePlus2, Droplet, Pencil, Trash2, ListChecks } from 'lucide-react';
import '../../styles/Pazienti.css';
import '../../styles/PazientiConsole.css';
import { notificaErrore, messaggioDa } from '../../utils/notifiche';
import useAzione from '../../navigazione/useAzione';
import useDalNumero from '../../navigazione/dalNumero';
import { FILTRO_DA_COMPLETARE } from '../../utils/anagraficheDaCompletare';

/* IL FILTRO «DA COMPLETARE» (Alex, 4/10/2026: «filtro Pazienti "da
   completare con appuntamento nei prossimi 7 giorni" (stessa regola del
   contatore)»). La regola non sta qui: la pagina chiede ?filtro=da-completare
   e il server legge la stessa condizione del numero accanto alla voce
   Pazienti del menu (services/anagraficheDaCompletare.js), quindi le righe e
   il numero coincidono. Solo per chi completa le anagrafiche, come il numero
   (admin e segreteria): agli altri il server risponde 403, e qui il pulsante
   non c'e'.
   Il filtro sta nell'INDIRIZZO (?filtro=da-completare), scritto sostituendo
   l'indirizzo e non aggiungendo un passo: una ricarica - anche quella della
   versione nuova - deve ritrovare l'elenco filtrato (CLAUDE.md, «quello che
   deve sopravvivere a una ricarica sta nell'indirizzo»). Un filtro che la
   pagina non sa usare si toglie dall'indirizzo, perche' l'indirizzo non dica
   un elenco diverso da quello a video.
   Il registro degli accessi: l'elenco filtrato e' la stessa lettura
   dell'elenco (elenco_pazienti/ricerca), una per apertura, nessuna in piu':
   all'apertura con ?filtro= la prima lettura e' gia' quella filtrata.
   Il valore del filtro sta in utils/anagraficheDaCompletare.js: lo stesso
   indirizzo lo apre il numero accanto alla voce Pazienti del menu (Alex,
   6/10/2026, risposta 22), e una copia qui lo farebbe divergere. */
const TITOLO_FILTRO = 'Da completare, con appuntamento nei prossimi 7 giorni';
const MSG_NESSUNA_DA_COMPLETARE = 'Nessuna anagrafica da completare fra chi ha un appuntamento nei prossimi 7 giorni.';
/* LA RIGA DEL FILTRO DICE IL VERO (SVILUPPO, 6/10/2026). Le righe sono
   quelle del numero accanto alla voce Pazienti del menu solo col filtro da
   solo: la ricerca e «Longevity» le restringono, e il numero del menu resta
   quello di tutte. Fino a quel giorno la frase delle «stesse» restava anche
   con «Longevity» acceso e un totale piu' piccolo, cioe' era falsa proprio
   quando qualcuno confrontava i due numeri.
   ACCENDERE IL FILTRO TOGLIE LA RICERCA (Alex, 7/10/2026, risposta B7: «clic
   su "Da completare" con una ricerca accesa toglie la ricerca: si'»): il clic
   sul numero del menu e quello sul pulsante «Da completare» che lo accende
   partono senza la parola scritta, cosi' le righe sono quelle del numero. La
   ricerca la restringe ancora se la si scrive DOPO, e «Longevity» resta com'e'
   (B7 parla della ricerca; Longevity e' un altro filtro, che si vede acceso):
   le due frasi qui sotto restano vere tutte e due. Spegnere il filtro non
   tocca la ricerca. */
const MSG_NOTA_INTERA = 'Sono le stesse del numero accanto alla voce Pazienti del menu.';
const MSG_NOTA_RISTRETTA = 'Il numero accanto alla voce Pazienti del menu le conta tutte, quindi può essere più alto.';

/* UN ELENCO CHE NON ARRIVA NON E' UN ELENCO VUOTO (revisione del 6/10/2026).
   Una ricerca fallita - il secondo di fermo di un riavvio, il Wi-Fi caduto -
   che scrivesse «Nessun paziente trovato» direbbe alla segreteria che il
   paziente non c'e', ed e' l'invito a un'accettazione doppia (CLAUDE.md: il
   risultato vuoto e' un esito da distinguere, non da restituire). Al posto
   delle righe c'e' una riga che dice che l'elenco non e' arrivato, con
   «Riprova». Una riga dentro la tabella e non una notifica: una notifica
   d'errore resta finche' qualcuno non la chiude e ferma la ricarica della
   versione nuova (versioneInLinea, guardia h; scenario 15, caso 8c, l'elenco
   chiesto senza rete). */
const MSG_ELENCO_NON_ARRIVATO = 'Non riesco a caricare l\'elenco dei pazienti: non vuol dire che non ci siano. Riprova fra un momento.';
const MSG_DA_COMPLETARE_NON_ARRIVATE = 'Non riesco a caricare le anagrafiche da completare: non vuol dire che non ce ne siano. Riprova fra un momento.';

const PazientiListConsole = () => {
    const [pazienti, setPazienti] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [showForm, setShowForm] = useState(false);
    const [selectedPaziente, setSelectedPaziente] = useState(null);
    const [showModulo, setShowModulo] = useState(false);
    const [modalitaModulo, setModalitaModulo] = useState('completo');
    const [soloLongevity, setSoloLongevity] = useState(false);
    const [pagina, setPagina] = useState(1);
    const [totale, setTotale] = useState(0);
    const PER_PAGINA = 100;
    const navigate = useNavigate();
    /* Le azioni della barra comandi (Ctrl K) e di «Nuovo»: lo stesso gesto
       del pulsante della pagina (navigazione/useAzione.js). */
    useAzione({
        accettazione: () => { setModalitaModulo('completo'); setShowModulo(true); },
        'accettazione-prelievi': () => { setModalitaModulo('prelievi'); setShowModulo(true); },
    });

    // L'esportazione dell'intera anagrafica e' riservata agli admin:
    // i pulsanti restano nascosti agli altri, che altrimenti
    // riceverebbero un errore di permessi senza capire perche'.
    const ruolo = useAuthStore((state) => state.user)?.ruolo;
    const isAdmin = ruolo === 'admin';
    /* Accettare ed eliminare un paziente sono lavoro di segreteria e
       amministrazione (Alex, 12/10/2026, domanda 18); modificare l'anagrafica
       lo fa anche il medico senior (Alex, 13/10/2026). Al medico l'elenco apre
       le cartelle e basta, senza pulsanti che porterebbero a un 403; al senior
       restano «Modifica» e non «Elimina». La protezione e' sul server. */
    const accettaPazienti = puoAccettarePazienti(ruolo);
    const modificaAnagrafica = puoModificareAnagrafica(ruolo);

    /* Il filtro «Da completare», dall'indirizzo (vedi in testa). */
    const completaAnagrafiche = FRONT_DESK.includes(ruolo);
    const [indirizzo, setIndirizzo] = useSearchParams();
    const filtroIndirizzo = indirizzo.get('filtro');
    const daCompletare = completaAnagrafiche && filtroIndirizzo === FILTRO_DA_COMPLETARE;
    const scriviFiltro = (attivo) => setIndirizzo((p) => {
        const n = new URLSearchParams(p);
        if (attivo) n.set('filtro', FILTRO_DA_COMPLETARE); else n.delete('filtro');
        return n;
    }, { replace: true });
    const cambiaFiltro = () => {
        setPagina(1);
        /* Accenderlo toglie la ricerca (B7, in testa): nello stesso gesto,
           quindi una lettura sola. */
        if (!daCompletare) setSearch('');
        scriviFiltro(!daCompletare);
    };
    /* Il clic sul numero accanto alla voce Pazienti toglie la ricerca, anche
       con la pagina gia' aperta (B7, in testa; navigazione/dalNumero.js). Nel
       disegno e non in un effetto, per la stessa ragione della pagina qui
       sotto: una lettura sola, gia' senza la parola. */
    const dalNumero = useDalNumero();
    if (dalNumero && search !== '') setSearch('');
    useEffect(() => {
        if (filtroIndirizzo !== null && !daCompletare) scriviFiltro(false);
        // eslint-disable-next-line
    }, [filtroIndirizzo, daCompletare]);

    /* Cercando si riparte dalla prima pagina: restare alla ventesima
       con tre risultati confonde.
       NEL RENDER, NON IN UN EFFETTO (revisione del 6/10/2026). Ogni lettura
       dell'elenco lascia una riga nel registro degli accessi
       (elenco_pazienti/ricerca). Con un effetto, il filtro acceso dal numero
       accanto alla voce Pazienti del menu - o spento dalla voce, o una
       ricerca scritta - stando alla pagina 3 faceva DUE letture: la prima
       con la pagina vecchia (offset 200), poi l'effetto rimetteva pagina 1 e
       si rileggeva. Il pulsante «Da completare» ne faceva una sola solo
       perche' rimette la pagina da se' (cambiaFiltro). Cambiando la pagina
       nel render, React rifa' subito il render prima di eseguire gli
       effetti, e la lettura parte una volta, gia' dalla prima pagina.
       Prova: components/guscio/numeroPazienti.test.js. */
    const criteri = `${search}|${soloLongevity}|${daCompletare}`;
    const [criteriDi, setCriteriDi] = useState(criteri);
    if (criteriDi !== criteri) {
        setCriteriDi(criteri);
        if (pagina !== 1) setPagina(1);
    }

    useEffect(() => {
        loadPazienti();
        // eslint-disable-next-line
    }, [search, soloLongevity, daCompletare, pagina]);

    /* L'ELENCO A VIDEO E' QUELLO DI CIO' CHE SI E' CHIESTO. Solo l'ultima
       richiesta scrive l'elenco: con il filtro una risposta arrivata in
       ritardo - l'elenco intero chiesto un attimo prima - finiva sotto il
       pulsante «Da completare» acceso, righe che il filtro non contiene e
       nessun errore. E fra il clic e la risposta non si mostra l'elenco di
       prima: `elencoDi` dice di quale richiesta sono le righe a video, e
       finche' non e' quella di adesso c'e' «Caricamento...». Senza, per un
       istante la riga del filtro diceva il totale dell'elenco intero. */
    const ultimaRichiesta = useRef(0);
    const chiave = `${search}|${soloLongevity}|${daCompletare}|${pagina}`;
    const [elencoDi, setElencoDi] = useState(null);
    /* L'elenco non e' arrivato: il messaggio da mostrare AL POSTO delle righe
       (vedi MSG_ELENCO_NON_ARRIVATO in testa), e il motivo se lo ha dato il
       server. null quando l'elenco a video e' quello arrivato. */
    const [nonArrivato, setNonArrivato] = useState(null);
    const aggiornato = !loading && elencoDi === chiave;
    /* Che cosa restringe l'elenco filtrato (vedi MSG_NOTA_RISTRETTA). Una
       ricerca di soli spazi non restringe: il server la ignora
       (ricercaTestuale, parole). */
    const restrizioni = [
        search.trim() !== '' && 'dalla ricerca',
        soloLongevity && 'da «Longevity»',
    ].filter(Boolean);
    const loadPazienti = async () => {
        const questa = ++ultimaRichiesta.current;
        try {
            setLoading(true);
            const parametri = {
                search,
                limit: PER_PAGINA,
                offset: (pagina - 1) * PER_PAGINA,
            };
            if (soloLongevity) parametri.longevity = true;
            if (daCompletare) parametri.filtro = FILTRO_DA_COMPLETARE;
            const response = await pazientiAPI.getAll(parametri);
            if (questa !== ultimaRichiesta.current) return;
            /* Una risposta senza esito non e' un elenco: prima lasciava a
               video le righe della richiesta di prima. */
            if (!response.data || !response.data.success) {
                throw Object.assign(new Error('elenco senza esito'), { response });
            }
            setPazienti(response.data.data.pazienti || []);
            setTotale(response.data.data.total || 0);
            setNonArrivato(null);
            setElencoDi(chiave);
        } catch (error) {
            if (questa !== ultimaRichiesta.current) return;
            console.error('Errore caricamento pazienti:', String(error && error.message));
            /* Le righe di prima non restano a video: sotto un filtro acceso
               (o appena spento) sarebbero righe di un'altra richiesta. Al
               loro posto la riga dell'elenco non arrivato, mai «Nessun
               paziente»; il numero sopra l'elenco filtrato non si scrive. Il
               motivo si mostra solo se l'ha scritto il server (un 429, un
               rifiuto): l'errore di rete del browser e' inglese e non dice
               niente in piu'. */
            const dalServer = error && error.response && error.response.data
                && typeof error.response.data.message === 'string' ? error.response.data.message.trim() : '';
            setPazienti([]);
            setTotale(0);
            setNonArrivato({
                messaggio: daCompletare ? MSG_DA_COMPLETARE_NON_ARRIVATE : MSG_ELENCO_NON_ARRIVATO,
                motivo: dalServer,
            });
            setElencoDi(chiave);
        } finally {
            if (questa === ultimaRichiesta.current) setLoading(false);
        }
    };

    const handleExportCSV = async () => {
        try {
            const response = await pazientiAPI.exportCSV();
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `pazienti-${oggiClinica()}.csv`);
            document.body.appendChild(link);
            link.click();
            link.remove();
        } catch (error) {
            console.error('Export CSV error:', error); notificaErrore(messaggioDa(error, 'Non riesco a esportare il CSV'));
        }
    };

    const handleExportVCard = async () => {
        try {
            const response = await pazientiAPI.exportVCard();
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `pazienti-${oggiClinica()}.vcf`);
            document.body.appendChild(link);
            link.click();
            link.remove();
        } catch (error) {
            notificaErrore(messaggioDa(error, 'Non riesco a esportare la vCard'));
        }
    };

    const handleDelete = async (e, p) => {
        e.stopPropagation();
        const msg =
            `Sei sicuro di voler eliminare il paziente ${p.nome} ${p.cognome}?\n\n` +
            `Verranno eliminati DEFINITIVAMENTE anche tutte le sue foto, documenti, ` +
            `procedure, anamnesi e alert.\n\n` +
            `Una volta eliminato NON sarà più recuperabile.`;
        if (window.confirm(msg)) {
            try {
                await pazientiAPI.delete(p.id);
                loadPazienti();
            } catch (error) {
                notificaErrore(messaggioDa(error, 'Non riesco a eliminare il paziente'));
            }
        }
    };

    const handleEdit = (e, paziente) => {
        e.stopPropagation();
        setSelectedPaziente(paziente);
        setShowForm(true);
    };

    const handleCloseForm = () => {
        setShowForm(false);
        setSelectedPaziente(null);
    };

    const handleFormSuccess = () => {
        loadPazienti();
    };

    return (
        <div className="pazienti-container">
            <div className="pazienti-header">
                <h1>Pazienti</h1>
                <div className="header-actions">
                    <input
                        type="text"
                        placeholder="Nome, cognome o codice fiscale"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        className="search-input"
                        data-non-trattiene
                    />
                    <button
                        onClick={() => setSoloLongevity((v) => !v)}
                        className={`btn-secondary ${soloLongevity ? 'btn-filtro-attivo' : ''}`}
                        title={soloLongevity ? 'Mostra tutti i pazienti' : 'Mostra solo i pazienti del percorso Longevity'}
                    >
                        <Clock size={15} strokeWidth={1.8} aria-hidden="true" />
                        {soloLongevity ? 'Solo Longevity' : 'Longevity'}
                    </button>
                    {completaAnagrafiche && (
                        /* data-non-trattiene: il valore sta nell'indirizzo,
                           una ricarica non lo perde (versioneInLinea). */
                        <button
                            type="button"
                            onClick={cambiaFiltro}
                            className={`btn-secondary ${daCompletare ? 'btn-filtro-attivo' : ''}`}
                            aria-pressed={daCompletare}
                            title={daCompletare ? 'Mostra tutti i pazienti' : TITOLO_FILTRO}
                            data-filtro={FILTRO_DA_COMPLETARE}
                            data-non-trattiene
                        >
                            <ListChecks size={15} strokeWidth={1.8} aria-hidden="true" />
                            Da completare
                        </button>
                    )}
                    {isAdmin && (
                        <>
                            <button onClick={handleExportCSV} className="btn-secondary">
                                <Download size={15} strokeWidth={1.8} aria-hidden="true" />
                                CSV
                            </button>
                            <button onClick={handleExportVCard} className="btn-secondary">
                                <Contact size={15} strokeWidth={1.8} aria-hidden="true" />
                                VCard
                            </button>
                        </>
                    )}
                    {/* L'accettazione e' l'unica strada per creare un
                        paziente: passa dai moduli e dalle firme, mentre il
                        vecchio «Nuovo paziente» inseriva un'anagrafica nuda
                        senza consensi. Il modulo resta raggiungibile per
                        MODIFICARE un paziente esistente, dall'icona della
                        riga. */}
                    {accettaPazienti && (
                        <>
                            <button onClick={() => { setModalitaModulo('completo'); setShowModulo(true); }} className="btn-primary">
                                <FilePlus2 size={15} strokeWidth={1.8} aria-hidden="true" />
                                Accettazione paziente
                            </button>
                            <button onClick={() => { setModalitaModulo('prelievi'); setShowModulo(true); }} className="btn-secondary btn-prelievi">
                                <Droplet size={15} strokeWidth={1.8} aria-hidden="true" />
                                Accettazione punto prelievi
                            </button>
                        </>
                    )}
                </div>
            </div>

            {daCompletare && (
                <p className="paz-filtro-nota" data-filtro-nota>
                    {TITOLO_FILTRO}{aggiornato && !nonArrivato && <>: <strong className="rm-numeri">{totale.toLocaleString('it-IT')}</strong></>}.
                    {' '}{restrizioni.length === 0
                        ? MSG_NOTA_INTERA
                        : `Ristrette ${restrizioni.join(' e ')}. ${MSG_NOTA_RISTRETTA}`}
                </p>
            )}

            {!aggiornato ? (
                <div className="loading">Caricamento...</div>
            ) : (
                <div className="table-container rm-scorre">
                    <table className="pazienti-table">
                        <thead>
                            <tr>
                                <th>ID</th>
                                <th>Nome</th>
                                <th>Cognome</th>
                                <th>Codice fiscale</th>
                                <th>Telefono</th>
                                <th>Email</th>
                                <th>Note</th>
                                {modificaAnagrafica && <th>Azioni</th>}
                            </tr>
                        </thead>
                        <tbody>
                            {nonArrivato ? (
                                /* L'elenco non e' arrivato: non e' vuoto (vedi
                                   MSG_ELENCO_NON_ARRIVATO in testa). */
                                <tr>
                                    <td colSpan={modificaAnagrafica ? 8 : 7} className="paz-elenco-non-arrivato">
                                        <div role="alert" data-elenco-non-arrivato className="paz-sul-bordo">
                                            <p>{nonArrivato.messaggio}</p>
                                            {nonArrivato.motivo && <p className="paz-elenco-motivo">{nonArrivato.motivo}</p>}
                                            <button type="button" className="btn-secondary" onClick={loadPazienti}>
                                                Riprova
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ) : pazienti.length === 0 ? (
                                <tr>
                                    <td colSpan={modificaAnagrafica ? 8 : 7} className="paz-vuoto">
                                        <span className="paz-sul-bordo">
                                            {search ? 'Nessun paziente trovato'
                                                : daCompletare ? MSG_NESSUNA_DA_COMPLETARE
                                                : (accettaPazienti
                                                    ? 'Nessun paziente. Clicca "Accettazione paziente" per registrarne uno.'
                                                    : 'Nessun paziente.')}
                                        </span>
                                    </td>
                                </tr>
                            ) : (
                                pazienti.map((p) => (
                                    <tr key={p.id} onClick={() => navigate(`/pazienti/${p.id}`)} style={{cursor: 'pointer'}}>
                                        <td className="cella-id">
                                            <span className="pid">{p.id_paziente}</span>
                                            <span className="simboli-paziente">
                                                {p.tipo_accettazione === 'prelievi' && (
                                                    <span className="rm-stato rm-stato-attenzione" title="Consensi non completi">Prelievi</span>
                                                )}
                                                {p.longevity === true && (
                                                    <span className="rm-stato rm-stato-accento">Longevity</span>
                                                )}
                                            </span>
                                        </td>
                                        <td className="cella-nome">{p.nome}</td>
                                        <td>{p.cognome}</td>
                                        <td>{p.codice_fiscale}</td>
                                        <td>{p.telefono || '-'}</td>
                                        <td>{p.email || '-'}</td>
                                        <td className="cella-note" onClick={(e) => e.stopPropagation()}>
                                            <NotaInline
                                                pazienteId={p.id}
                                                valoreIniziale={p.note}
                                                compatta
                                                onSaved={(nuovo) => { p.note = nuovo; }}
                                            />
                                        </td>
                                        {modificaAnagrafica && (
                                            <td>
                                                <div className="action-buttons">
                                                    <button onClick={(e) => handleEdit(e, p)} className="btn-edit" title="Modifica"><Pencil size={15} strokeWidth={1.8} aria-hidden="true" /></button>
                                                    {accettaPazienti && (
                                                        <button onClick={(e) => handleDelete(e, p)} className="btn-delete" title="Elimina"><Trash2 size={15} strokeWidth={1.8} aria-hidden="true" /></button>
                                                    )}
                                                </div>
                                            </td>
                                        )}
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>

                    {totale > PER_PAGINA && (
                        <div className="paz-pagine">
                            <button disabled={pagina <= 1} onClick={() => setPagina(pagina - 1)}>
                                ‹ Precedente
                            </button>
                            <span>
                                {(pagina - 1) * PER_PAGINA + 1}–{Math.min(pagina * PER_PAGINA, totale)}
                                {' di '}{totale.toLocaleString('it-IT')}
                            </span>
                            <button disabled={pagina * PER_PAGINA >= totale} onClick={() => setPagina(pagina + 1)}>
                                Successiva ›
                            </button>
                        </div>
                    )}
                </div>
            )}

            {modificaAnagrafica && showForm && (
                <PazienteForm
                    paziente={selectedPaziente}
                    onClose={handleCloseForm}
                    onSuccess={handleFormSuccess}
                />
            )}
            {accettaPazienti && showModulo && (
                <ModulisticaWizard
                    modalita={modalitaModulo}
                    onClose={() => setShowModulo(false)}
                    onSuccess={loadPazienti}
                />
            )}
        </div>
    );
};

export default PazientiListConsole;
